import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, FileText, Loader2, ReceiptText } from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (amount, currency = "PHP") => {
  const symbol = currency === "PHP" ? "₱" : `${currency} `;
  return `${symbol}${Number(amount || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function ReceiptView() {
  const { receiptType, referenceId } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/receipts/${receiptType}/${referenceId}`);
        setReceipt(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Receipt not available");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [receiptType, referenceId]);

  const detailRows = useMemo(() => {
    if (!receipt?.booking_details) return [];
    return Object.entries(receipt.booking_details).filter(([, value]) => value);
  }, [receipt]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  }

  if (!receipt) {
    return <div className="max-w-3xl mx-auto px-4 py-20 text-center font-heading text-xl">Receipt not available</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-6" data-testid="receipt-view-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="receipt-back-btn">
            <ArrowLeft className="w-4 h-4" />Back
          </button>
          <div className="flex items-center gap-3">
            <ReceiptText className="w-8 h-8 text-court-orange" />
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">{receipt.title}</h1>
              <p className="text-muted-foreground text-sm">Receipt ID: {receipt.receipt_id}</p>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.print()} className="rounded-full border-2" data-testid="receipt-print-btn">
            <Download className="w-4 h-4 mr-2" />Download / Print PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2"><FileText className="w-5 h-5" />Receipt Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <Badge className="bg-green-100 text-green-800 uppercase">Paid</Badge>
              <span className="text-sm text-muted-foreground">Issued: {receipt.payment_details?.paid_at?.slice(0, 10) || receipt.issued_at?.slice(0, 10)}</span>
            </div>

            <div>
              <p className="text-xs font-heading uppercase tracking-[0.15em] text-muted-foreground mb-3">Booking Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {detailRows.map(([key, value]) => (
                  <div key={key} className="rounded-lg border border-slate-200 p-3" data-testid={`receipt-detail-${key}`}>
                    <p className="text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground mb-1">{key.replace(/_/g, " ")}</p>
                    <p className="font-medium break-words">{String(value)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-heading uppercase tracking-[0.15em] text-muted-foreground mb-3">Line Items</p>
              <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="receipt-line-items-table">
                <div className="grid grid-cols-[2fr_80px_120px_120px] bg-slate-50 text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground px-4 py-3">
                  <span>Description</span>
                  <span className="text-center">Qty</span>
                  <span className="text-right">Unit Price</span>
                  <span className="text-right">Total</span>
                </div>
                {receipt.line_items?.map((item, idx) => (
                  <div key={`${item.label}-${idx}`} className="grid grid-cols-[2fr_80px_120px_120px] px-4 py-3 text-sm border-t border-slate-100" data-testid={`receipt-line-item-${idx}`}>
                    <div>
                      <p className="font-medium">{item.label}</p>
                      <p className="text-xs text-muted-foreground uppercase">per {item.unit_label}</p>
                    </div>
                    <span className="text-center">{item.quantity}</span>
                    <span className="text-right">{formatCurrency(item.unit_price, receipt.currency)}</span>
                    <span className="text-right font-medium">{formatCurrency(item.line_total, receipt.currency)}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Totals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3" data-testid="receipt-totals-card">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span>{formatCurrency(receipt.subtotal, receipt.currency)}</span></div>
            {receipt.discount_amount > 0 && <div className="flex justify-between text-sm text-green-700"><span>Discount</span><span>-{formatCurrency(receipt.discount_amount, receipt.currency)}</span></div>}
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Amount before VAT</span><span>{formatCurrency(receipt.amount_before_vat, receipt.currency)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">VAT (12%)</span><span>{formatCurrency(receipt.vat_amount, receipt.currency)}</span></div>
            <div className="border-t pt-3 flex justify-between text-lg font-heading font-bold"><span>Total Paid</span><span className="text-court-orange">{formatCurrency(receipt.total_amount, receipt.currency)}</span></div>

            <div className="rounded-xl border border-slate-200 p-4 space-y-2 text-sm" data-testid="receipt-payment-meta">
              <div className="flex justify-between gap-3"><span className="text-muted-foreground">Payment Method</span><span className="text-right">{receipt.payment_details?.payment_method}</span></div>
              <div className="flex justify-between gap-3"><span className="text-muted-foreground">Payment Ref</span><span className="text-right break-all">{receipt.payment_details?.payment_reference}</span></div>
              <div className="flex justify-between gap-3"><span className="text-muted-foreground">Transaction Ref</span><span className="text-right break-all">{receipt.payment_details?.transaction_reference}</span></div>
            </div>

            {receipt.notes?.length > 0 && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
                {receipt.notes.map((note, idx) => <p key={idx}>{note}</p>)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}